// frontend/src/components/CreateApiKeyModal.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import { VALID_SCOPES } from "../api/apiKeys";

/**
 * CreateApiKeyModal — form to mint a new API key.
 *
 * Inputs: Name (required), Scopes (checkboxes for each VALID_SCOPES entry),
 * Expiry date (optional; blank means the key never expires). On submit it calls
 * `onCreate({ name, scopes, expiresAt })` and lets the parent handle the
 * one-time key reveal.
 *
 * @param {object}   props
 * @param {Function} props.onCreate  async ({name, scopes, expiresAt}) => void
 * @param {Function} props.onClose   close the modal without creating
 * @param {boolean}  [props.creating] disables inputs while the request is in flight
 */
function CreateApiKeyModal({ onCreate, onClose, creating }) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState([]);
  const [expiresAt, setExpiresAt] = useState("");

  const toggleScope = (scope) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    onCreate({
      name: name.trim(),
      scopes,
      // A date-only value (yyyy-mm-dd) is sent as end-of-day UTC; blank = null.
      expiresAt: expiresAt ? `${expiresAt}T23:59:59Z` : null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 mb-4">
          Create New API Key
        </h2>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label
              htmlFor="apikey-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name
            </label>
            <input
              id="apikey-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
              placeholder="e.g. CI/CD Pipeline"
              disabled={creating}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
            />
          </div>

          {/* Scopes */}
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">
              Scopes
            </legend>
            <div className="space-y-2">
              {VALID_SCOPES.map((scope) => (
                <label
                  key={scope}
                  className="flex items-center gap-2 text-sm text-gray-700"
                >
                  <input
                    type="checkbox"
                    checked={scopes.includes(scope)}
                    onChange={() => toggleScope(scope)}
                    disabled={creating}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <code className="font-mono text-xs">{scope}</code>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Expiry */}
          <div>
            <label
              htmlFor="apikey-expiry"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Expiry date{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              id="apikey-expiry"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={creating}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
            />
            <p className="mt-1 text-xs text-gray-400">
              Leave blank for a key that never expires.
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating || !name.trim()}
              className="px-4 py-2 text-sm rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? "Creating…" : "Create key"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

CreateApiKeyModal.propTypes = {
  onCreate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  creating: PropTypes.bool,
};
CreateApiKeyModal.defaultProps = { creating: false };

export default CreateApiKeyModal;
